import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit, Trash2, Building, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const CMSServices = () => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    nom: '',
    type: 'Santé',
    adresse: '',
    tel: '',
    statut_verification: 'Non renseigné'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/services`);
      setServices(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (service) => {
    setFormData({
      id: service.id,
      nom: service.nom,
      type: service.type,
      adresse: service.adresse,
      tel: service.tel,
      statut_verification: service.statut_verification
    });
    setIsEditing(true);
  };

  const handleNew = () => {
    setFormData({
      id: '',
      nom: '',
      type: 'Santé',
      adresse: '',
      tel: '',
      statut_verification: 'Non renseigné'
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment désactiver ce service ?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/services/${id}`, { withCredentials: true });
        toast.success('Service supprimé');
        fetchServices();
      } catch (e) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, is_active: true };
      
      if (formData.id) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/services/${formData.id}`, payload, { withCredentials: true });
        toast.success('Service mis à jour');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/services`, payload, { withCredentials: true });
        toast.success('Service ajouté');
      }
      
      setIsEditing(false);
      fetchServices();
    } catch (e) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F] flex items-center gap-2">
            <Building className="w-6 h-6 text-blue-600" /> Gestion de l'Annuaire (CMS)
          </h1>
          <p className="text-slate-500 font-medium">Administration des structures de services</p>
        </div>
        {!isEditing && (
          <Button onClick={handleNew} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="ajouter-service-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un service
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8">
          <div className="p-5 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900">{formData.id ? 'Modifier le service' : 'Nouveau service'}</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="service-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nom de la structure *</label>
                <input required type="text" name="nom" value={formData.nom} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" data-testid="input-nom" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Type (Secteur) *</label>
                <select required name="type" value={formData.type} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" data-testid="select-type">
                  <option value="Santé">Santé</option>
                  <option value="Éducation">Éducation</option>
                  <option value="Social">Action Sociale</option>
                  <option value="Administration">Administration</option>
                  <option value="Sécurité">Sécurité</option>
                  <option value="Justice">Justice</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Téléphone *</label>
                <input required type="text" name="tel" value={formData.tel} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" placeholder="Si inconnu, mettre 'Non renseigné'" data-testid="input-tel" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Adresse complète *</label>
                <input required type="text" name="adresse" value={formData.adresse} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" data-testid="input-adresse" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Statut de vérification de la fiche *</label>
                <input required type="text" name="statut_verification" value={formData.statut_verification} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" placeholder="Ex: Vérifié le 10/10/2023, Non renseigné" data-testid="input-statut-verif" />
              </div>
            </div>
            <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>Annuler</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-900 text-white" data-testid="service-form-submit">
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
             <div className="p-12 text-center flex items-center justify-center">
               <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
             </div>
          ) : services.length === 0 ? (
            <div className="p-12 text-center">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Aucun service dans l'annuaire</h3>
              <p className="text-slate-500">Commencez par ajouter une première structure.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                    <th className="px-6 py-4">Structure</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Contact</th>
                    <th className="px-6 py-4">Vérification</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {services.map(service => (
                    <tr key={service.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900 text-sm mb-1">{service.nom}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[200px]">{service.adresse}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                          {service.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-700">{service.tel}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs font-medium ${service.statut_verification.includes('Vérifié') ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {service.statut_verification}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(service)} className="h-8 w-8 text-blue-600 hover:bg-blue-50" data-testid={`service-edit-btn-${service.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(service.id)} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid={`service-delete-btn-${service.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CMSServices;